import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useQueryGetPayments } from '../../../../../../queries/paymentQueries'
import {
  getPaymentStatusMeta,
  getStatusBadgeClass,
  historyTableStyles
} from './historyTableUtils'

const PaymentHistory = () => {
  const { data: payments = [], isLoading } = useQueryGetPayments()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const formatDate = dateString => {
    if (!dateString) return 'N/A'

    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatAmount = amount => {
    if (!amount) return '$0'

    const numAmount = parseFloat(amount)

    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(numAmount)
  }

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <div className='h-12 w-12 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
      </div>
    )
  }

  if (payments.length === 0) {
    return (
      <div className='rounded-2xl border border-dashed border-gray-200 bg-gray-50/70 py-10 text-center text-sm text-gray-500'>
        No hay pagos registrados
      </div>
    )
  }

  if (isMobile) {
    return (
      <div className='space-y-3'>
        {payments.map(payment => {
          const statusMeta = getPaymentStatusMeta(payment)

          return (
            <article
              key={payment.id}
              className='rounded-2xl border border-gray-100 bg-white p-4 shadow-sm'
            >
              <div className='flex items-start justify-between gap-3'>
                <div>
                  <p className='text-xs font-bold text-slate-800'>{formatDate(payment.createdAt)}</p>
                  <p className='mt-1 text-[11px] text-gray-500'>Pago #{payment.id}</p>
                </div>
                <span className={getStatusBadgeClass(statusMeta.tone)}>{statusMeta.label}</span>
              </div>

              <div className='mt-3 space-y-3'>
                <div>
                  <p className='text-[10px] uppercase tracking-wider text-gray-400'>Descripcion</p>
                  <p className='text-sm font-semibold text-slate-800'>
                    {payment.description || 'Sin descripcion'}
                  </p>
                </div>

                <div>
                  <p className='text-[10px] uppercase tracking-wider text-gray-400'>Tarjeta</p>
                  {payment.card ? (
                    <div>
                      <p className='text-sm font-semibold text-slate-800'>
                        {payment.card.name || payment.card.cardType?.toUpperCase() || 'Tarjeta'}
                      </p>
                      <p className='text-xs text-gray-500'>**** {payment.card.lastFourDigits}</p>
                    </div>
                  ) : (
                    <p className='text-sm text-gray-500'>Sin tarjeta asociada</p>
                  )}
                </div>

                <div>
                  <p className='text-[10px] uppercase tracking-wider text-gray-400'>Monto</p>
                  <p className='text-base font-bold text-slate-900'>{formatAmount(payment.amount)}</p>
                </div>
              </div>
            </article>
          )
        })}
      </div>
    )
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-lg font-semibold text-gray-800'>Historial de pagos</h3>
        <span className='text-xs font-medium uppercase tracking-[0.18em] text-gray-400'>
          {payments.length} registros
        </span>
      </div>

      <div className='overflow-auto rounded-2xl'>
        <table className={historyTableStyles.table}>
          <thead className={historyTableStyles.thead}>
            <tr>
              <th className={historyTableStyles.th}>Fecha</th>
              <th className={historyTableStyles.th}>Descripcion</th>
              <th className={historyTableStyles.th}>Tarjeta</th>
              <th className={historyTableStyles.th}>Monto</th>
              <th className={historyTableStyles.th}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(payment => {
              const statusMeta = getPaymentStatusMeta(payment)

              return (
                <tr key={payment.id} className={historyTableStyles.tr}>
                  <td className={historyTableStyles.td}>
                    <div className='flex flex-col items-center justify-center'>
                      <span className={historyTableStyles.boldText}>
                        {formatDate(payment.createdAt)}
                      </span>
                      <span className={historyTableStyles.text}>Pago #{payment.id}</span>
                    </div>
                  </td>
                  <td className={historyTableStyles.td}>
                    <span className={historyTableStyles.boldText}>
                      {payment.description || 'Sin descripcion'}
                    </span>
                  </td>
                  <td className={historyTableStyles.td}>
                    {payment.card ? (
                      <div className='flex flex-col items-center justify-center'>
                        <span className={historyTableStyles.boldText}>
                          {payment.card.name || payment.card.cardType?.toUpperCase() || 'Tarjeta'}
                        </span>
                        <span className={historyTableStyles.text}>
                          **** {payment.card.lastFourDigits}
                        </span>
                      </div>
                    ) : (
                      <span className={historyTableStyles.text}>Sin tarjeta asociada</span>
                    )}
                  </td>
                  <td className={historyTableStyles.td}>
                    <span className='text-sm font-bold text-slate-900'>
                      {formatAmount(payment.amount)}
                    </span>
                  </td>
                  <td className={historyTableStyles.td}>
                    <span className={getStatusBadgeClass(statusMeta.tone)}>{statusMeta.label}</span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default PaymentHistory
